import {AGENCY_KEYS, useAgencyPreRegisterDetailsQuery} from "@/api/agencyApi";
import BuildingIcon from "@/assets/colorable/building.svg?react";
import KeyIcon from "@/assets/colorable/key.svg?react";
import TickIcon from "@/assets/colorable/tick_medium.svg?react";
import RefluencedLogo from "@/assets/images/refluenced.svg";
import Button from "@/components/Common/Button";
import TextButton from "@/components/Common/TextButton";
import {useLogin} from "@/hooks/useLogin";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import RegisterAgency_01 from "@/pages/agencies/RegisterAgency_01";
import RegisterAgency_02 from "@/pages/agencies/RegisterAgency_02";
import {useCreateAgencyStore} from "@/store/createAgency";
import useRegistrationStore from "@/store/registration";
import {useMutation} from "@tanstack/react-query";
import {createFileRoute} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";
import {z} from "zod";

export const Route = createFileRoute("/_unauthenticated/register/agency")({
  component: RegisterAgency,
  validateSearch: zodValidator(
    z.object({
      uuid: z.string().optional(),
    }),
  ),
});

function RegisterAgency() {
  const {t} = useTranslation(["multi_account", "common", "navigation"]);

  useTitle(t("navigation:register"));

  const navigate = Route.useNavigate();
  const {uuid} = Route.useSearch();
  const {data: preRegisterDetails} = useAgencyPreRegisterDetailsQuery({uuid: uuid!});
  const setter = useCreateAgencyStore(state => state.setter);
  const validate = useCreateAgencyStore(state => state.validateRegisterAgencySchema);
  const [step, setStep] = useState(1);

  useEffect(() => {
    const unsubscribe = useRegistrationStore.subscribe((state, prevState) => {
      if (state.password !== prevState.password) {
        useCreateAgencyStore.getState().setter(draft => {
          draft.details.password = state.password;
        });
      }

      if (state.selectedLearnAboutUs !== prevState.selectedLearnAboutUs) {
        useCreateAgencyStore.getState().setter(draft => {
          draft.details.howDidYouHearAboutUs = state.selectedLearnAboutUs;
        });
      }

      if (state.learnAboutUsExtra !== prevState.learnAboutUsExtra) {
        useCreateAgencyStore.getState().setter(draft => {
          draft.details.howDidYouHearAboutUsExtra = state.learnAboutUsExtra;
        });
      }

      if (state.phoneNumber !== prevState.phoneNumber) {
        useCreateAgencyStore.getState().setter(draft => {
          draft.details.phoneNumber = state.phoneNumber;
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);
  useEffect(() => {
    setter(state => {
      state.form.agency_name = preRegisterDetails?.agency_name ?? "";
      state.form.address = preRegisterDetails?.address ?? "";
      state.form.postal_code = preRegisterDetails?.postal_code ?? "";
      state.form.city = preRegisterDetails?.city ?? "";
      state.form.website = preRegisterDetails?.website ?? "";
      state.form.country = preRegisterDetails?.country ?? "Switzerland";
      state.form.first_name = preRegisterDetails?.first_name ?? "";
      state.form.last_name = preRegisterDetails?.last_name ?? "";
      state.form.main_user_email = preRegisterDetails?.main_user_email ?? "";
    });
  }, [setter, preRegisterDetails]);

  const {asyncLogin, isPending: loginIsPending} = useLogin();
  const {mutateAsync, isPending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: AGENCY_KEYS.mAgencyRegister,
    mutationFn: async () => {
      const details = useCreateAgencyStore.getState().details;
      const form = useCreateAgencyStore.getState().form;
      const registrationValues = useRegistrationStore.getState();
      const formData = new FormData();

      formData.append("uuid", uuid!);
      formData.append("email", preRegisterDetails?.main_user_email!);
      formData.append("password", registrationValues.password);
      formData.append("first_name", form.first_name);
      formData.append("last_name", form.last_name);
      formData.append("name", form.agency_name!);
      formData.append("address", form.address!);
      formData.append("city", form.city!);
      formData.append("postal_code", form.postal_code!);
      formData.append("website", form.website!);
      formData.append("phonenumber", details.phoneNumber);
      formData.append("country", form.country);

      // Add username temporarily
      formData.append("username", "asdf1234");

      if (details.logo) {
        formData.append("picture", details.logo);
        formData.append("picture320", details.logo);
      }

      if (details.profilePicture) {
        formData.append("profile_picture", details.profilePicture);
        formData.append("profile_picture320", details.profilePicture);
      }

      const learnAboutUs = useRegistrationStore.getState().selectedLearnAboutUs;
      const learnAboutUsExtra = useRegistrationStore.getState().learnAboutUsExtra;

      formData.append("learn_about_us", learnAboutUs!);
      formData.append("learn_about_us_name", learnAboutUsExtra);
      return await client.post(AGENCY_KEYS.mAgencyRegister[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
    },
    onSuccess: async () => {
      await asyncLogin(
        {
          email: preRegisterDetails?.main_user_email!,
          password: useRegistrationStore.getState().password,
        },
        {onSuccess: () => navigate({to: "/"})},
      );

      useCreateAgencyStore.getState().reset();
      useRegistrationStore.getState().reset();
    },
  });

  return (
    <div className="w-full px-4 sm:px-8 md:px-12">
      <div className="flex w-full flex-col items-center sm:px-20 lg:px-8 xl:px-20">
        <div className="flex flex-row items-center gap-3">
          {step === 1 ? (
            <button
              onClick={() => setStep(1)}
              className="border-primary-medium-solid bg-primary-dark-solid flex size-10 items-center justify-center rounded-full border p-2.5 text-white">
              <BuildingIcon className="size-8" />
            </button>
          ) : (
            <button
              onClick={() => setStep(1)}
              className="border-success-light-green bg-success-herbal flex size-10 items-center justify-center rounded-full border p-2.5 text-white">
              <TickIcon className="size-8" />
            </button>
          )}
          <div className="bg-neutral-grey-light h-1 w-4 rounded-full" />
          <button
            onClick={() => setStep(2)}
            className={clsxm(
              "flex size-10 items-center justify-center rounded-full border p-2.5",
              step === 2
                ? "border-primary-medium-solid bg-primary-dark-solid text-white"
                : "border-neutral-grey-light bg-primary-light-solid text-neutral-grey-light",
            )}>
            <KeyIcon className="size-10" />
          </button>
        </div>
        <div className="mt-10 flex flex-col items-center">
          <div className="mb-5 size-20 shrink-0">
            <img src={RefluencedLogo} className="size-full object-contain" />
          </div>
          <p className="text-neutral-black text-sm font-semibold">
            {step === 1
              ? t(
                  preRegisterDetails?.large_brand
                    ? "multi_account:set_up_enterprise_account"
                    : "multi_account:set_up_agency_account",
                )
              : t("multi_account:owner_setup")}
          </p>
        </div>
        {step === 1 && <RegisterAgency_01 />}
        {step === 2 && <RegisterAgency_02 />}
        {step === 1 ? (
          <Button
            dataTestId="register-agency-next-button"
            onClick={() => setStep(2)}
            className="mt-10 h-14 w-48"
            buttonStyle="Blue">
            {t("common:next")}
          </Button>
        ) : (
          <Button
            dataTestId="register-agency-create-button"
            onClick={async () => {
              if (validate()) {
                await mutateAsync();
              }
            }}
            isLoading={isPending || loginIsPending}
            className="mt-10 h-14 w-48"
            buttonStyle="Gradient">
            {t("multi_account:create_account")}
          </Button>
        )}
        <div className="mt-5 flex flex-col gap-5 text-center">
          {step === 2 && (
            <div className="text-neutral-grey-dark text-center text-sm font-medium">
              {t("common:createAccountDisclaimer")}
              <TextButton
                onClick={() => {
                  window.open("/terms-conditions/", "_blank");
                }}
                text={"Refluenced " + t("common:ToS")}></TextButton>{" "}
              {t("common:and")}{" "}
              <TextButton
                className="text-primary-dark-solid hover:text-primary-medium-solid"
                onClick={() => {
                  window.open("/privacy-policy/", "_blank");
                }}
                text={t("common:privacy_policy")}
              />
              {t("common:allowContact")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
